<template>
  <ModalBase @close-modal="closeEventModal">
    <div class="modalLayout" v-if="liveStatus.status !== 'after'">
      <div class="modalContainer">
        <div class="modalInfo" v-if="hasInformation">
          <div class="modalInfo_error" v-if="isError">
            <p>
              チェックイン時にエラーが発生しました。大変恐れ入りますが、時間をおいてから再度視聴ボタンを押してください。
            </p>
          </div>
          <p class="modalInfo_time">{{ time }}</p>
          <p class="modalInfo_theme">
            【{{
              selectedHospital.hospital_name
                ? selectedHospital.hospital_name
                : 'マイナビRESIDENT運営事務局'
            }}】{{ selectedProgram.title }}
          </p>

          <div class="button_warpper" v-if="zoomUrl">
            <ZoomButton
              :label="zoomButtonProps.label"
              :handleClick="zoomButtonProps.handler"
              :disabled="zoomButtonProps.disabled"
            />
          </div>
          <p
            class="modalInfo_description"
            v-if="selectedProgram.description"
            v-html="selectedProgram.description"
          />

          <template v-if="zoomUrl">
            <div class="eventPrecautionForJoin_wrap">
              <EventPrecautionForJoin :precautionHtml="precautionHtml" />
            </div>

            <div class="button_warpper">
              <ZoomButton
                :label="zoomButtonProps.label"
                :handleClick="zoomButtonProps.handler"
                :disabled="zoomButtonProps.disabled"
              />
            </div>
          </template>
        </div>

        <section class="modalSection" v-if="isShowHospitalCassette">
          <HospitalCassette
            class="modalSection_hospitalInfo"
            :detail="selectedHospital"
            :isShowFeatures="false"
            :isShowRequestBtn="true"
          />
        </section>

        <section class="modalSection" v-if="hasMovie">
          <HeadingLv3 class="modalSection_ttl" text="病院紹介動画" />
          <div style="max-width: 406px">
            <div class="modalSection_hospitalMovie">
              <iframe
                style="position:absolute;top:0;left:0;width:100%;height:100%;"
                :src="
                  `https://www.youtube.com/embed/${selectedProgram.hospital_movie_id}`
                "
                title="YouTube video player"
                frameborder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowfullscreen
              ></iframe>
            </div>
          </div>
        </section>

        <section class="modalSection" v-if="recommendHospitals.length">
          <HeadingLv3
            class="modalSection_ttl"
            text="条件の似ている病院の配信予定"
          />
          <ul class="hospitalCassetteList">
            <HospitalCassetteOnlineEvent
              class="hospitalCassetteList_item"
              v-for="item in recommendHospitals.slice(0, 4)"
              :key="`${item.hospital_id}_${item.start}`"
              :liveStatus="item.liveStatus"
              :programStartTime="item.programStartTime"
              :title="item.title"
              :label="item.label"
              :channelName="item.channel_name"
              :channelDescription="item.channel_description"
              :hospitalStart="item.hospital_start"
              :hospitalEnd="item.hospital_end"
              :detail="item.hospital"
              :enable-request="false"
              :viewLive="viewLive"
            />
          </ul>
        </section>
      </div>
    </div>
    <div v-if="liveStatus.status === 'after'">
      <Alert text="配信は終了しました。" />
      <button type="button" class="closeText" @click="closeModal">
        画面を閉じて別の配信を確認する
      </button>
    </div>
  </ModalBase>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  PropType,
  watch,
  nextTick
} from '@vue/runtime-core'
import ModalBase from '../genenral/ModalBase.vue'
import HeadingLv3 from '@/components/elements/HeadingLv3.vue'
import HospitalCassette from '@/components/block/HospitalCassette.vue'
import Alert from '@/components/elements/Alert.vue'
import HospitalCassetteOnlineEvent from '@/components/block/HospitalCassetteOnlineEvent.vue'
import EventPrecautionForJoin from '@/components/block/EventPrecautionForJoin.vue'
import ZoomButton from '@/components/block/ZoomButton.vue'
import dayjs from 'dayjs'
import { useStore } from 'vuex'
dayjs.locale('ja')

export default defineComponent({
  name: 'ModalOnlineEvent',

  components: {
    ModalBase,
    HeadingLv3,
    HospitalCassette,
    Alert,
    HospitalCassetteOnlineEvent,
    EventPrecautionForJoin,
    ZoomButton
  },

  props: {
    selectedProgram: {
      type: Object,
      default: () => ({})
    },
    selectedHospital: {
      type: Object,
      default: () => ({})
    },
    recommendHospitals: {
      type: Array,
      default: () => []
    },
    liveStatus: {
      type: Object,
      default: () => ({})
    },
    precautionHtml: {
      type: String,
      default: ''
    },
    closeEventModal: {
      type: Function as PropType<() => void>,
      required: true
    },
    applyCheckin: {
      type: Function as PropType<
        (type: 'event' | 'hospital', hospital_id?: string) => void
      >,
      required: true
    },
    viewLive: {
      type: Function as PropType<
        (channelName: string, selectedProgramStart: string) => void
      >,
      required: true
    },
    currentTime: {
      type: String,
      required: true
    },
    isCheckInError: {
      type: Boolean,
      required: true
    }
  },

  setup(props) {
    const store = useStore()

    const closeModal = () => {
      store.commit('unFixBody')
      props.closeEventModal()
    }

    // vimeoCheckinは不要だが、watchし続けないと、モーダルが閉じるのでそのまま
    const vimeoCheckin = () => {
      if (
        !props.selectedProgram.zoom_url &&
        props.selectedProgram.movie_url &&
        props.selectedProgram.hospital_id &&
        props.liveStatus.status === 'live'
      ) {
        // サーバー側で、イベントチェックイン処理もまとめて行う applyCheckin('event')
        props.applyCheckin('hospital', props.selectedProgram.hospital_id)
      }
    }
    vimeoCheckin()

    watch(
      () => [props.selectedProgram, props.liveStatus.status],
      () => {
        vimeoCheckin()
        if (props.liveStatus.status === 'after') {
          nextTick(() => {
            setTimeout(() => {
              closeModal()
            }, 1000)
          })
        }
      }
    )

    const time = computed((): string => {
      if (Object.keys(props.selectedProgram).length === 0) return ''
      const program = props.selectedProgram
      return `${program.start_hour}:${program.start_minute} - ${program.end_hour}:${program.end_minute}`
    })

    const hasInformation = computed((): boolean => {
      return Object.keys(props.selectedProgram).length > 0
    })

    const hasMovie = computed((): boolean => {
      return (
        Object.keys(props.selectedProgram).length > 0 &&
        props.selectedProgram.hospital_movie_id
      )
    })

    const isShowHospitalCassette = computed(
      (): boolean =>
        Object.keys(props.selectedHospital).length > 0 &&
        !props.selectedProgram.is_organization_online //WP"団体として追加する"にチェック済していない
    )

    const vimeoUrl = computed(() => {
      return (
        Object.keys(props.selectedProgram).length > 0 &&
        props.selectedProgram.movie_url
      )
    })
    const zoomUrl = computed(() => {
      return (
        Object.keys(props.selectedProgram).length > 0 &&
        props.selectedProgram.zoom_url
      )
    })

    const zoomButtonHandler = () => {
      // サーバー側で、イベントチェックイン処理もまとめて行う applyCheckin('event')
      props.applyCheckin('hospital', props.selectedProgram.hospital_id)
      window.open(zoomUrl.value, '_blank')
    }
    const zoomButtonProps = computed(() => {
      let label = `${props.selectedProgram.eventDate} ${props.selectedProgram.start}から配信予定`
      let disabled = true
      if (['live', 'soon'].includes(props.liveStatus.status)) {
        label = 'LIVE配信中 視聴はこちら'
        disabled = false
      }
      return {
        label,
        disabled,
        handler: zoomButtonHandler
      }
    })

    const isError = computed(() => props.isCheckInError)
    return {
      time,
      hasInformation,
      hasMovie,
      isShowHospitalCassette,
      vimeoUrl,
      zoomUrl,
      zoomButtonProps,
      closeModal,
      isError
    }
  }
})
</script>

<style lang="scss" scoped>
@use 'src/styles/abstracts/mixins';
@use 'src/styles/abstracts/variables' as var;

::v-deep .modal {
  height: calc(100% + 40px);
  width: 100%;
  max-width: 1088px;
  margin-top: 20px;
  padding-right: 0;
  padding-left: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  @include mixins.media() {
    height: 100%;
    margin-top: 0;
    margin-left: 20px;
    margin-right: 20px;
    padding-left: 40px;
    padding-right: 15px;
  }
}
.modalLayout {
  width: 100%;
  height: 100%;
  overflow-y: scroll;

  @include mixins.media() {
    padding-right: 30px;
    padding-left: 12px;
  }
}

.modalContainer {
  padding-left: 16px;
  padding-right: 16px;
  padding-bottom: 24px;
  max-width: 744px;
  margin-left: auto;
  margin-right: auto;
}

.modalInfo {
  &_time {
    font-weight: 500;
  }
  &_theme {
    font-weight: 500;
    font-size: 1.8rem;
    line-height: 1.5;
    margin-top: 8px;
    + .button_warpper {
      margin-top: 28px;
    }
  }

  &_description {
    font-size: 1.3rem;
    margin-top: 28px;
  }
  &_error {
    padding: 10px 15px;
    line-height: 1.78;
    border: 1px solid #f64545;
    color: #f64545;
    margin-bottom: 16px;
  }
}
::v-deep(.modalInfo_description > a) {
  color: var.$color-main-text;
}

.modalSection {
  margin-top: 32px;

  &_ttl {
    margin-bottom: 16px;
  }

  &_hospitalMovie {
    padding-top: 56.25%;
    position: relative;
  }

  &_hospitalInfo {
    margin-top: -8px;
  }
}

.modalPlayer {
  margin-bottom: 20px;

  @include mixins.media() {
    display: flex;
    margin-bottom: 40px;
  }

  &_main {
    padding-top: 56.25%;
    position: relative;

    @include mixins.media() {
      padding-top: calc(100% * 0.666 * 0.5625);
      flex-basis: 66.666%;
    }
  }

  &_chat {
    width: 100%;
    height: 400px;

    @include mixins.media() {
      height: auto;
      flex-basis: 33.333%;
    }
  }
}

.hospitalCassetteList {
  @include mixins.media() {
    margin-top: -8px;
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
  }

  &_item {
    margin-top: 16px;

    @include mixins.media() {
      flex-basis: calc(50% - 10px);
      max-width: calc(50% - 10px);
      min-width: calc(50% - 10px);
      margin-top: 24px;
    }
  }
}

.closeText {
  color: var.$color-main-text;
  text-decoration: underline;

  &:hover {
    text-decoration: none;
  }
}
.button_warpper {
  background-color: var.$color-other-bg;
  padding: 16px;
  &:not(:first-child) {
    margin-top: 32px;
  }
}
.eventPrecautionForJoin_wrap {
  margin-top: 16px;
}
</style>
